/**
 * Per-file redacted-output staleness for the folder view (VE-25870). Same verdict
 * as the media details tab, from a folder row instead of the mediaDetails store.
 * No editor session here, so unsaved edits aren't a factor.
 *
 * `collectBatesBehindTdoIds` (VE-30105) answers a second question from the same
 * rows: does each member's output still carry its merged document's numbering?
 * Not derived from redaction data — the file has not changed, the set's order
 * or stamp has — but the same remedy clears it, so it feeds the same badge.
 *
 * `collectWatermarksBehindTdoIds` (VE-30104) is the third question and the
 * same shape: does the output carry the set's current WATERMARKS? Simpler than
 * the numbering one, because a watermark is identical on every member — no
 * position arithmetic, no order to reconcile, and no orphan case, since a
 * former member's leftover mark collides with nothing where leftover numbers
 * land on another member's range.
 *
 * Those two collectors answer for a LIST, which is what the editor's panel and
 * the redact-all pass want. VE-31438 added the per-file route beside them:
 * `getFolderFileStaleness` takes an optional `MergedSetStaleness` and folds
 * both verdicts into the file's own reasons. Needed because the two surfaces
 * that gate a DOWNLOAD — the folder row's menu and the Download Review modal —
 * ask about one file at a time and had been releasing members whose numbering
 * and marking the set had moved on from. Omit the set and the verdict is
 * exactly what it was before.
 *
 * The version label where the save carries one — since VE-26422 that is both
 * media and documents — and a timestamp otherwise: did the newest save land
 * after the output was created? A redaction-count comparison used to fill that
 * second case and is gone. It could not see a redaction merely moved or resized,
 * and for documents it flattened four redaction kinds into one total.
 *
 * The timestamp is anchored on the output rather than on the burn-in's own
 * assets, which is what makes it safe here without the tolerance the media
 * details tab needs. An output exists only once the engine or the renderer has
 * finished, minutes after anything the same click wrote, so the race between a
 * save and a burn-in's own writes cannot reach across that gap.
 *
 * `createdDateTime` specifically: `modifiedDateTime` moves whenever an asset's
 * metrics are rewritten, and a download does exactly that, so anchoring on it
 * would push the mark forward and hide a save that came after the burn-in.
 *
 * The gap it accepts: a save made while the burn-in was still running lands
 * before the output and reads as included. The row warns for the duration
 * anyway, since a running redact task is its own reason.
 *
 * Reads whatever assets the row carries. The folder-open query deliberately
 * fetches only some of them — callers that need a current answer refresh the row
 * first (see `judgeFiles` in FolderColumn), which is how the download paths get a
 * verdict that describes the file now rather than at page load.
 *
 * Also home to `isFileRedactionRunning`, the row's "Redacting" test: a running
 * redact-engine task, or — for the browser-side image and document burn-ins,
 * which create no task (VE-28061) — the `redactFile` duck's in-flight entry.
 */
import {
  computeRedactedFileStaleness,
  NOT_STALE,
  pickNewestBy,
  pickNewestRedactionData,
  readBurnInLabel,
  readRedactionProvenance,
  RedactedFileStaleness,
  type StaleReason,
} from '@helpers/redactedFileStaleness';
import { isRedactTaskInFlight } from '@helpers/redactTaskStatus';
import type { RedactFilesState } from '@common-modules/redactFile';

import {
  batesNumberingVerdict,
  type BatesVerdict,
} from '@redact-modules/projects/batesStamp';
import {
  watermarkStalenessFromIdentity,
  type WatermarkVerdict,
} from '@redact-modules/projects/projectRedactionAsset';
import type { MergedSetStaleness } from '@redact-modules/projects/mergedSetStaleness';
import {
  readAssetBatesProjectModifiedDateTime,
  readAssetWatermarkProjectModifiedDateTime,
  readAssetBatesSpanMismatch,
  readAssetBatesStartNumber,
} from '@redact-modules/documents/helpers';
import { AssetFileType } from './store';

/**
 * The in-memory per-TDO burn-in status the `redactFile` duck keeps
 * (`selectRedactFiles`): `running` from the click until the output lands.
 *
 * VE-30516: an alias for the duck's own type rather than a second description
 * of it. The hand-written copy this replaced still read `{ status: string }`
 * after the duck had gained two more fields — structurally compatible, so it
 * kept compiling while quietly describing the wrong shape.
 */
export type RedactFilesStatus = RedactFilesState;

/**
 * True when a redaction is in flight for this file, from either signal:
 *
 * - the redact ENGINE has an unfinished task on it (video/audio). Only the most
 *   recent redact task counts, and the status check is `isRedactTaskInFlight` —
 *   an inclusion list, so an unfamiliar status stays quiet. Shared with the
 *   dashboard's `redactFile` latch since VE-30516, which asked the opposite
 *   question and pinned failed redactions on "Redacting" for ever;
 * - the app has a browser-side burn-in running for it (images, and documents
 *   since VE-28061). Those never create an engine task, so the `redactFile`
 *   duck is the only record of them — and they keep running after the editor is
 *   closed, which is exactly when a list row needs to say "Redacting".
 *
 * Deliberately not the shared `isRedactionRunning` selector: its branch A matches
 * on `task.engine.id` (folder rows carry a flat `engineId`), and branch B then
 * returns true for any running job, cognition engines included.
 */
/**
 * The slice of a file record the running check reads. Structural and readonly
 * so both the folder row (`AssetFileType`) and the drawer's `SummaryTDO` job
 * tasks fit without copying.
 */
export interface RedactionRunningFileShape {
  readonly id?: string;
  readonly tasks?: {
    readonly records?: ReadonlyArray<{
      readonly engineId?: string;
      readonly status?: string;
    } | null> | null;
  } | null;
}

export const isFileRedactionRunning = (
  file: RedactionRunningFileShape | null | undefined,
  redactEngineId: string | undefined,
  redactFiles?: RedactFilesStatus
): boolean => {
  if (file?.id && redactFiles?.[file.id]?.status === 'running') {
    return true;
  }
  if (!redactEngineId) {
    return false;
  }
  const latestRedactTask = file?.tasks?.records?.find(
    (task) => task?.engineId === redactEngineId
  );
  if (!latestRedactTask) {
    return false;
  }
  return isRedactTaskInFlight(latestRedactTask.status);
};

/**
 * The row's newest redacted output. `createdDateTime` is the burn-in;
 * `modifiedDateTime` moves later when the asset's metrics are rewritten, so it
 * is only a fallback for older assets that lack the former.
 */
export const pickNewestOutput = (file: AssetFileType | null | undefined) =>
  pickNewestBy(
    file?.redactedMediaAssets?.records,
    (record) => record.createdDateTime || record.modifiedDateTime
  );

export interface FolderFileStaleness extends RedactedFileStaleness {
  /** The output the verdict is about, for naming it in the UI. */
  readonly output: AssetFileType | null;
}

/**
 * The set's verdicts, folded into a file's staleness.
 *
 * ONE FOLD, because the mapping from verdict to reason is a decision — which
 * of the four Bates verdicts warns, that `unknown` warns about nothing — and
 * it was about to exist twice: once in the editor's banner and once in the
 * folder view's download gate, judging the same file on two surfaces a
 * reviewer flips between.
 *
 * `unknown` adds nothing: a check that could not run must not raise a warning
 * and must not clear one either.
 */
export const withSetVerdicts = (
  staleness: RedactedFileStaleness,
  verdicts: {
    readonly bates?: BatesVerdict;
    readonly watermark?: WatermarkVerdict;
  }
): RedactedFileStaleness => {
  const extra: StaleReason[] = [];
  if (verdicts.bates === 'behind') {
    extra.push('batesNumberingBehind');
  } else if (verdicts.bates === 'orphaned') {
    extra.push('batesNumberingOrphaned');
  } else if (verdicts.bates === 'strayNumbering') {
    extra.push('batesNumberingStray');
  }
  if (verdicts.watermark === 'behind') {
    extra.push('watermarksBehind');
  }
  return extra.length === 0
    ? staleness
    : {
        ...staleness,
        isStale: true,
        reasons: [...staleness.reasons, ...extra],
      };
};

/**
 * One file's verdicts against the set it belongs to.
 *
 * SEPARATE FROM THE PER-FILE CHECK because they answer a different question:
 * that one asks whether the output is behind the file's own saved redactions,
 * and these ask whether it is behind the SET. A reviewer about to release it
 * needs both, and one Redact File fixes either, which is why they travel one
 * verdict rather than a second warning.
 */
const setVerdictsFor = (
  file: AssetFileType,
  output: AssetFileType,
  set: MergedSetStaleness
): { bates: BatesVerdict; watermark: WatermarkVerdict } => {
  const id = String(file.id);
  return {
    bates: batesNumberingVerdict({
      hasOutput: true,
      membershipKnown: set.members !== undefined,
      isMember: !!set.members?.has(id),
      projectModifiedDateTime: set.batesModifiedDateTime,
      expectedStart: set.expectedStartById?.get(id) ?? null,
      carriedModifiedDateTime: readAssetBatesProjectModifiedDateTime(
        output.details
      ),
      carriedStart: readAssetBatesStartNumber(output.details),
      spanMismatch: readAssetBatesSpanMismatch(output.details),
    }),
    watermark: watermarkStalenessFromIdentity({
      hasOutput: true,
      isMember: set.members ? set.members.has(id) : undefined,
      identity: set.watermarksModifiedDateTime,
      carriedModifiedDateTime: readAssetWatermarkProjectModifiedDateTime(
        output.details
      ),
    }),
  };
};

export const getFolderFileStaleness = (
  file: AssetFileType | null | undefined,
  redactEngineId: string | undefined,
  /**
   * The merged set this file may belong to. Omitted where the caller has not
   * read it — the verdict then describes the file alone, which is what every
   * caller got before this existed.
   */
  mergedSet?: MergedSetStaleness
): FolderFileStaleness => {
  const output = pickNewestOutput(file);
  if (!output) {
    return { ...NOT_STALE, output: null };
  }

  const redactionData = file?.redactionDataAssets?.records;
  // `details` is typed for input-file fields; an output asset also carries the
  // burn-in `version`, which isn't in that shape.
  const version = (output.details as Record<string, unknown> | undefined)?.[
    'version'
  ];

  // A trimmed `redacted-media-clip` carries no version: the number is a position
  // in the patch chain and a clip has no place in that sequence, so the engine
  // leaves it off. The selections asset the clip names records the version Redact
  // File asked for, which is the one we actually want, so it stands in. Without
  // it a trimmed row is judged by counts and shows no version in Download Review.
  const consumedSelections = readRedactionProvenance(output.details)
    .redactionAssetId
    ? file?.redactionSelectionAssets?.records?.find(
        (record) =>
          record.id === readRedactionProvenance(output.details).redactionAssetId
      )
    : undefined;
  const selectionsVersion =
    consumedSelections?.details?.['expectedRedactedVersion'];

  const newestSave = pickNewestRedactionData(redactionData);
  const label = readBurnInLabel(newestSave?.details);
  const outputVersion =
    typeof version === 'number'
      ? version
      : typeof selectionsVersion === 'number'
        ? selectionsVersion
        : undefined;

  /**
   * A labelled save answers outright: it names the output version it was sent to
   * produce, so the file is current exactly when that is the output on hand, and
   * an ordinary save was never sent at all.
   *
   * A save written before labelling falls back to timestamps — did anything land
   * after the output existed? Anchored on the output rather than on the burn-in's
   * own assets, which is what makes it safe without the tolerance the media
   * details tab needs: an output appears only once the engine or the renderer has
   * finished, minutes after any asset written by the same click, so the race
   * between a save and a burn-in's own writes cannot reach across that gap.
   *
   * `createdDateTime` specifically. `modifiedDateTime` moves whenever an asset's
   * metrics are rewritten, and a download does exactly that, so anchoring on it
   * would push the mark forward and hide a save that came after the burn-in.
   *
   * The gap it accepts: a save made while the burn-in was still running lands
   * before the output and reads as included. The row warns for the duration
   * anyway, since a running redact task is its own reason.
   */
  const savedAfter = (() => {
    if (label.state === 'burnedIn' && outputVersion !== undefined) {
      return label.version !== outputVersion;
    }
    if (label.state === 'saved') {
      return true;
    }
    const savedAt = newestSave?.createdDateTime;
    const burnedInAt = output.createdDateTime;
    return (
      !!savedAt &&
      !!burnedInAt &&
      new Date(savedAt).getTime() > new Date(burnedInAt).getTime()
    );
  })();

  const own = computeRedactedFileStaleness({
    hasOutput: true,
    savedAfterBurnIn: savedAfter,
    isBurnInRunning: isFileRedactionRunning(file, redactEngineId),
    version: outputVersion,
  });
  return {
    ...(mergedSet && file
      ? withSetVerdicts(own, setVerdictsFor(file, output, mergedSet))
      : own),
    output,
  };
};

/**
 * The files whose redacted output no longer contains their saved redactions.
 */
export const collectStaleOutputTdoIds = (
  files: readonly (AssetFileType | null | undefined)[],
  redactEngineId: string | undefined
): ReadonlySet<string> => {
  const ids = new Set<string>();
  for (const file of files) {
    if (!file) {
      continue;
    }
    if (
      getFolderFileStaleness(file, redactEngineId).reasons.includes(
        'missingLatestEdits'
      )
    ) {
      ids.add(String(file.id));
    }
  }
  return ids;
};

export interface BatesBehindFiles {
  /** Members whose output does not carry the set's current numbering. */
  readonly behind: ReadonlySet<string>;
  /** Former members still carrying numbering from a set they have left. */
  readonly orphaned: ReadonlySet<string>;
}

/**
 * Which files' outputs are behind a merged document's numbering, and which are
 * orphaned (VE-30105).
 *
 * A ROW MAPPER over `batesNumberingVerdict`, which is the one implementation
 * of the rule. The editor's banner calls the same function from session data.
 * They used to have one each and the orphan rules had already drifted.
 *
 * `members === undefined` means the membership has not been READ. Nothing is
 * reported in that state: treating it as an empty set made every stamped file
 * in the request orphaned for as long as the read was outstanding.
 */
export const collectBatesBehindTdoIds = (
  files: readonly (AssetFileType | null | undefined)[],
  /**
   * When the set's Bates settings last changed; `undefined` when the record
   * has not been read. Read from the project asset's `details`, so deciding
   * this for a whole column of rows never downloads a body.
   */
  projectBatesModifiedDateTime: string | null | undefined,
  expectedStartById?: ReadonlyMap<string, number | null>,
  members?: ReadonlySet<string>
): BatesBehindFiles => {
  const behind = new Set<string>();
  const orphaned = new Set<string>();
  const membershipKnown = !!members;
  for (const file of files) {
    const output = pickNewestOutput(file);
    if (!file || !output) {
      continue;
    }
    const id = String(file.id);
    const verdict = batesNumberingVerdict({
      hasOutput: true,
      membershipKnown,
      isMember: !!members?.has(id),
      // PASSED THROUGH, not `?? null`: `undefined` means the record has not
      // been read and `null` means the set carries no stamping. Collapsing them
      // reported every member current while the lookup was outstanding.
      projectModifiedDateTime: projectBatesModifiedDateTime,
      expectedStart: expectedStartById?.get(id) ?? null,
      // The SHARED reader, which tolerates a `details` that arrived as a JSON
      // string. Reading the field raw here gave the panel and the editor
      // opposite answers on the same asset.
      carriedModifiedDateTime: readAssetBatesProjectModifiedDateTime(
        output.details
      ),
      carriedStart: readAssetBatesStartNumber(output.details),
      spanMismatch: readAssetBatesSpanMismatch(output.details),
    });
    if (verdict === 'behind') {
      behind.add(id);
    } else if (verdict === 'orphaned' || verdict === 'strayNumbering') {
      // ONE SET, because this collector answers "what does a re-burn fix" and
      // the answer is the same for both. Only the banner needs them apart,
      // and it computes its own verdict rather than reading this.
      orphaned.add(id);
    }
  }
  return { behind, orphaned };
};

/**
 * Which members' outputs are missing the merged document's current watermarks
 * (VE-30104).
 *
 * A ROW MAPPER, like `collectBatesBehindTdoIds` above, and simpler for the
 * reason the watermark verdict is simpler: a watermark is identical on every
 * member, so there is no position arithmetic to feed it and no order to
 * reconcile — only the stamp the output recorded against the one the set
 * holds now.
 *
 * NEEDED BECAUSE APPLYING MARKS TO THE SET WRITES NOTHING TO A MEMBER. Its
 * redaction data, its timestamps and its save label all stand still, so every
 * other staleness signal on this row reports it current while the release it
 * belongs to has moved on.
 *
 * Over `watermarkStalenessFromIdentity` since VE-31438, rather than the
 * open-coded comparison it used to carry. That copy agreed with the rule only
 * by inspection, and the two had already drifted on what a missing stamp
 * means for a file that never carried one.
 */
export const collectWatermarksBehindTdoIds = (
  files: readonly (AssetFileType | null | undefined)[],
  /**
   * The set's watermark STAMP — `null` when the set carries no marking,
   * `undefined` when the record has not been read. Read from the project
   * asset's `details`, so a whole column is judged without downloading a body.
   */
  projectWatermarkModifiedDateTime: string | null | undefined,
  /** The set's members. `undefined` while the membership is unread. */
  members?: ReadonlySet<string>
): ReadonlySet<string> => {
  const behind = new Set<string>();
  for (const file of files) {
    const output = pickNewestOutput(file);
    if (!file || !output) {
      continue;
    }
    const id = String(file.id);
    const verdict = watermarkStalenessFromIdentity({
      hasOutput: true,
      isMember: members ? members.has(id) : undefined,
      identity: projectWatermarkModifiedDateTime,
      carriedModifiedDateTime: readAssetWatermarkProjectModifiedDateTime(
        output.details
      ),
    });
    if (verdict === 'behind') {
      behind.add(id);
    }
  }
  return behind;
};
